import { SetMirrorData } from '@type/job-add';
import {
  RefObject,
  useState, useEffect, useRef, forwardRef, useImperativeHandle,
} from 'react';
import {
  Label, Button, Input,
  RecordInterface,
} from '@components/common/Forms';
import { TailwindSpinner } from '@components/common';
import { LexicalEditorType, LexicalEditorImperative } from '@type/lexical';
import { Lexical } from '@components/common/Forms/Editor/Lexical';
import { ArrowPathIcon, NoSymbolIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';
import { useLexicalValidity } from '@hooks/useBjForm';
import { useLLMDescription } from '@hooks/useLLMData/useLLMDescription';
import { dataTestId } from '@services/data-test-id';
import { JobDescriptionOverLayer } from './partials/JobDescriptionOverLayer';

/** Props */
interface JobDescriptionProps {
  record: RecordInterface,
  locale: Locale;
  imperativeRef: RefObject<LexicalEditorImperative | null>,
  jobTitle?: string;
  defaultValue?: string;
  setMirrorData?: SetMirrorData,
}

/**
 * @description Job Description component. To be used in a form. It includes an AI description generator.
 * It also includes a hidden input for the AI generated description and duration.
 * - `record` - record function from useBjForm
 * - `locale` - the current locale
 * - `imperativeRef` - imperative ref for the Lexical editor instance
 * - `jobTitle` - the current job title
 * - `defaultValue` - default value
 * - `setMirrorData` - function to update the mirror form state
 */
export const JobDescription = forwardRef<LexicalEditorType | null, JobDescriptionProps>((
  props,
  ref,
) => {
  // Destructure props
  const {
    locale,
    record,
    imperativeRef,
    jobTitle,
    defaultValue,
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Helpers
  const [showOverLayer, setShowOverLayer] = useState<boolean>(!defaultValue);
  const hasInteracted = useRef<boolean>(false);
  const editorRef = ref as unknown as RefObject<LexicalEditorType | null>;
  const autoGenerateDesc = useRef<boolean>(!defaultValue);

  // Lexical validity
  const {
    htmlContent,
    setHtmlContent,
    setTextContent,
    isValid,
    hasFocus,
    getLexicalContent: getContent,
    checkLexicalValidity: checkValidity,
  } = useLexicalValidity(editorRef?.current);

  // AI description
  const {
    generateAiDescription,
    stopGeneratingAiDescription,
    aiDescription,
    isGenerating,
    setIsGenerating,
    aiGenerationDuration,
  } = useLLMDescription({
    URL: '/job/ws',
  });

  // Update the mirror data after manual editing
  useEffect(() => {
    if (hasFocus) hasInteracted.current = true;
    if (hasInteracted.current && hasFocus === false && !isGenerating) {
      setMirrorData?.({ description: htmlContent }, false, true);
    }
  }, [hasFocus, isGenerating, setMirrorData, htmlContent]);

  // Update the mirror data after AI description is generated
  useEffect(() => {
    if (isGenerating === false && aiDescription) {
      setMirrorData?.({ description: htmlContent }, false, true);
      // Prevent double call
      setIsGenerating(undefined);
    }
  }, [aiDescription, isGenerating, setMirrorData, setIsGenerating, htmlContent]);

  // Expose imperative methods to parent
  useImperativeHandle(imperativeRef, () => ({
    checkLexicalValidity: checkValidity,
    getLexicalContent: getContent,
  }));

  // Trigger AI description generation on adding a title the first time
  // if no description or no AI description is present
  useEffect(() => {
    if (jobTitle && autoGenerateDesc.current) {
      const descriptionContent = getContent();
      if (!descriptionContent) {
        setShowOverLayer(false);
        autoGenerateDesc.current = false;
        generateAiDescription({
          title: jobTitle,
          language: locale,
        });
      }
    }
  }, [jobTitle, getContent, generateAiDescription, locale]);

  // Render
  // **************************
  return (
    <>
      {/* Description */}
      <Label
        required
        as="div"
        description={t('job.add.label.desc')}
        descriptionClass="font-bold text-ink-medium px-3.5"
      >
        {/* Description tip */}
        <span className="mb-3 block px-3.5 text-sm leading-snug text-ink-light">
          {t('job.add.tip.desc')}
        </span>

        <div className="relative">
          {/* Generate description over layer */}
          {!defaultValue && showOverLayer && (
            <JobDescriptionOverLayer
              isValid={isValid}
              overLayerSetter={setShowOverLayer}
              generateAiDescription={() => {
                if (jobTitle) {
                  generateAiDescription({
                    title: jobTitle,
                    language: locale,
                  });
                }
              }}
            />
          )}

          {/* Show if AI is generating */}
          {isGenerating && (
            <div className="pointer-events-none absolute inset-1 z-1 flex items-end justify-end rounded-md">
              <div className="flex w-full items-center justify-end gap-2 bg-surface-50 px-3 py-1.5 text-sm">
                <TailwindSpinner className="size-4 text-secondary" />
                {t('openai-add-job-generating')}
              </div>
            </div>
          )}

          {/* Description editor; we add key to rerender on defaultValue change */}
          <Lexical
            required
            defaultValue={aiDescription ?? defaultValue}
            handleHtmlDescription={setHtmlContent}
            handleTextDescription={setTextContent}
            autoFocus={false}
            ref={editorRef}
            isValid={isGenerating ? true : isValid}
            {...dataTestId('jobAddPage.jobDescription.container')}
          />
        </div>
      </Label>

      {/* Action button */}
      <div className="-mt-1 flex flex-wrap justify-end gap-2 md:px-3.5">
        {/* AI stop description */}
        {isGenerating && (
          <Button
            color="light"
            styling="soft"
            size="sm"
            rounding="full"
            className="gap-2 !py-1.5 !pl-3.5 !pr-4 !font-normal leading-tight"
            onClick={stopGeneratingAiDescription}
            {...dataTestId('jobAddPage.jobDescription.stopAiGenerateButton')}
          >
            <NoSymbolIcon className="size-4" />
            {t('openai.add.job.stop.generating')}
          </Button>
        )}

        {/* AI regenerate description */}
        {!showOverLayer && !isGenerating && jobTitle && (
          <Button
            color="light"
            styling="soft"
            size="sm"
            rounding="full"
            className="gap-2 !py-1.5 !pl-3.5 !pr-4 !font-normal leading-tight"
            onClick={() => {
              if (jobTitle) {
                generateAiDescription({
                  title: jobTitle,
                  language: locale,
                  existing_content: aiDescription,
                });
              }
            }}
            {...dataTestId('jobAddPage.jobDescription.regenerateDescription')}
          >
            <ArrowPathIcon className="size-4" />
            {t('openai.add.job.regenerate.description')}
          </Button>
        )}
      </div>

      {/* Hidden inputs for AI statistics */}
      {aiDescription && (
        <div>
          <Input
            type="hidden"
            value={aiGenerationDuration}
            {...record('aiGenerationDuration')}
          />
          <Input
            type="hidden"
            value={isGenerating ? '' : aiDescription}
            {...record('aiGeneratedDescription')}
          />
        </div>
      )}
    </>
  );
});
